import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Ticket, Plus, Trash2, Power } from 'lucide-react';
import {
  Coupon,
  CouponType,
  COUPON_TYPE_LABELS,
  createCoupon,
  getCoupons,
  setCouponActive,
  deleteCoupon,
  normalizeCode,
} from '../services/couponService';

const Container = styled.div`
  padding: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 28px;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 18px 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
`;

const Field = styled.div<{ $full?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => (props.$full ? 'grid-column: 1 / -1;' : '')}
`;

const Label = styled.label`
  font-size: 0.82rem;
  color: #666;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.9rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 0.9rem;
  font-family: inherit;
  background: white;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const Hint = styled.span`
  font-size: 0.75rem;
  color: #999;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #1a1a1a;
  color: white;
  border: 1px solid #1a1a1a;
  border-radius: 3px;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 18px;

  &:hover:not(:disabled) {
    background: #000;
  }

  &:disabled {
    background: #cfcfcf;
    border-color: #cfcfcf;
    cursor: not-allowed;
  }
`;

const CouponList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CouponRow = styled.div<{ $inactive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: ${props => (props.$inactive ? '#fafafa' : 'white')};
  opacity: ${props => (props.$inactive ? 0.65 : 1)};
  flex-wrap: wrap;
`;

const CodeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 170px;
`;

const Code = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #1a1a1a;
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: #777;
`;

const UsageBar = styled.div`
  flex: 1;
  min-width: 140px;
`;

const BarTrack = styled.div`
  height: 5px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
`;

const BarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${props => Math.min(100, props.$pct)}%;
  background: ${props => (props.$pct >= 100 ? '#b3242f' : '#1a1a1a')};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${props => (props.$danger ? '#dc3545' : '#ccc')};
  color: ${props => (props.$danger ? '#dc3545' : '#444')};
  border-radius: 3px;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$danger ? '#dc3545' : '#1a1a1a')};
    color: white;
    border-color: ${props => (props.$danger ? '#dc3545' : '#1a1a1a')};
  }
`;

const EmptyMessage = styled.p`
  color: #888;
  font-size: 0.92rem;
  text-align: center;
  padding: 32px 0;
`;

const emptyForm = {
  code: '',
  type: 'percent' as CouponType,
  value: 10,
  totalQuantity: 50,
  minAmount: 0,
  expiresAt: '',
};

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    setLoading(true);
    setCoupons(await getCoupons());
    setLoading(false);
  };

  const handleCreate = async () => {
    setSaving(true);
    const result = await createCoupon({
      code: form.code,
      type: form.type,
      value: Number(form.value),
      totalQuantity: Number(form.totalQuantity),
      minAmount: Number(form.minAmount),
      isActive: true,
      expiresAt: form.expiresAt || null,
    });
    setSaving(false);

    if (!result.success) {
      alert(result.error || 'Kupon oluşturulamadı.');
      return;
    }

    setForm(emptyForm);
    loadCoupons();
  };

  const handleToggle = async (coupon: Coupon) => {
    const ok = await setCouponActive(coupon.code, !coupon.isActive);
    if (ok) loadCoupons();
    else alert('Kupon durumu değiştirilemedi.');
  };

  const handleDelete = async (coupon: Coupon) => {
    if (!window.confirm(`"${coupon.code}" kuponu silinecek. Onaylıyor musunuz?`)) return;
    const ok = await deleteCoupon(coupon.code);
    if (ok) loadCoupons();
    else alert('Kupon silinemedi.');
  };

  const describe = (c: Coupon) => {
    const parts: string[] = [];
    parts.push(c.type === 'percent' ? `%${c.value} indirim` : 'Ücretsiz kargo');
    if (c.minAmount > 0) parts.push(`min. ${c.minAmount.toLocaleString('tr-TR')} TL`);
    if (c.expiresAt) parts.push(`son gün ${c.expiresAt}`);
    return parts.join(' · ');
  };

  return (
    <Container>
      <Header>
        <Title>
          <Ticket size={26} />
          Kupon Yönetimi
        </Title>
      </Header>

      <Card>
        <CardTitle>Yeni Kupon Oluştur</CardTitle>
        <FormGrid>
          <Field>
            <Label>Kupon Kodu</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: normalizeCode(e.target.value) })}
              placeholder="BAHAR25"
              maxLength={24}
            />
            <Hint>Harf, rakam ve tire</Hint>
          </Field>

          <Field>
            <Label>Kampanya Türü</Label>
            <Select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as CouponType })}
            >
              {(Object.keys(COUPON_TYPE_LABELS) as CouponType[]).map(t => (
                <option key={t} value={t}>{COUPON_TYPE_LABELS[t]}</option>
              ))}
            </Select>
          </Field>

          {form.type === 'percent' && (
            <Field>
              <Label>İndirim Yüzdesi</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
              />
              <Hint>1 - 100 arası</Hint>
            </Field>
          )}

          <Field>
            <Label>Kullanım Adedi</Label>
            <Input
              type="number"
              min={1}
              value={form.totalQuantity}
              onChange={(e) => setForm({ ...form, totalQuantity: Number(e.target.value) })}
            />
            <Hint>Kaç kez kullanılabilir</Hint>
          </Field>

          <Field>
            <Label>Minimum Sepet Tutarı</Label>
            <Input
              type="number"
              min={0}
              value={form.minAmount}
              onChange={(e) => setForm({ ...form, minAmount: Number(e.target.value) })}
            />
            <Hint>0 = sınır yok</Hint>
          </Field>

          <Field>
            <Label>Son Kullanma Tarihi</Label>
            <Input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
            <Hint>Boş = süresiz</Hint>
          </Field>
        </FormGrid>

        <PrimaryButton onClick={handleCreate} disabled={saving || !form.code}>
          <Plus size={16} />
          {saving ? 'Oluşturuluyor...' : 'Kuponu Oluştur'}
        </PrimaryButton>
      </Card>

      <Card>
        <CardTitle>Mevcut Kuponlar ({coupons.length})</CardTitle>

        {loading ? (
          <EmptyMessage>Yükleniyor...</EmptyMessage>
        ) : coupons.length === 0 ? (
          <EmptyMessage>Henüz kupon oluşturulmamış.</EmptyMessage>
        ) : (
          <CouponList>
            {coupons.map(coupon => {
              const pct = coupon.totalQuantity
                ? (coupon.usedCount / coupon.totalQuantity) * 100
                : 0;
              const exhausted = coupon.usedCount >= coupon.totalQuantity;

              return (
                <CouponRow key={coupon.code} $inactive={!coupon.isActive || exhausted}>
                  <CodeBlock>
                    <Code>{coupon.code}</Code>
                    <Meta>{describe(coupon)}</Meta>
                  </CodeBlock>

                  <UsageBar>
                    <Meta>
                      {coupon.usedCount} / {coupon.totalQuantity} kullanıldı
                      {exhausted && ' — hakkı doldu'}
                      {!coupon.isActive && ' — kapalı'}
                    </Meta>
                    <BarTrack>
                      <BarFill $pct={pct} />
                    </BarTrack>
                  </UsageBar>

                  <Actions>
                    <IconButton onClick={() => handleToggle(coupon)}>
                      <Power size={14} />
                      {coupon.isActive ? 'Kapat' : 'Aç'}
                    </IconButton>
                    <IconButton $danger onClick={() => handleDelete(coupon)}>
                      <Trash2 size={14} />
                      Sil
                    </IconButton>
                  </Actions>
                </CouponRow>
              );
            })}
          </CouponList>
        )}
      </Card>
    </Container>
  );
};

export default CouponManagement;
